/**
 * DevicesSection — the tills registered to this business.
 *
 * Exists because a device row was previously created on first sync and then
 * lived forever, invisible. Clearing a browser's storage mints a NEW device id
 * while the old row keeps its billing slot, so a merchant could be locked out
 * of their own quota by a browser they no longer use, with no way to see it and
 * no way to fix it.
 *
 * Revoking frees the slot. That is safe only because a device taking a used
 * slot continues its number series rather than restarting — the sequence is
 * recovered from the highest number already issued on that (store, slot). See
 * billing/invoiceNumberRecovery.ts.
 */
import { notifySuccess } from '@/store/useToastStore';
import { useErrorPopup } from '@/hooks/useErrorPopup';
import { useCallback, useEffect, useState } from 'react';
import { Laptop, Smartphone, Monitor, RotateCw } from 'lucide-react';
import { http } from '@/services/httpClient';
import { useAppStore } from '@/store/useAppStore';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Code } from '@/components/ui/numeric';
import { Explain, ExplainRow } from '@/components/ui/Explain';
import { cn } from '@/utils/cn';

interface DeviceRow {
  id: string;
  deviceName: string | null;
  platform: string | null;
  appVersion: string | null;
  storeId: string | null;
  deviceSlot: number | null;
  isActive: boolean;
  lastSyncAt: string | null;
  createdAt: string;
}

export function DevicesSection() {
  const currentDeviceId = useAppStore((s) => s.deviceId);
  const role = useAppStore((s) => s.currentUser?.role);
  const isOwner = role === 'OWNER';

  const [devices, setDevices] = useState<DeviceRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useErrorPopup(error, () => setError(null));
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await http<{ devices: DeviceRow[] }>('/auth/devices', { method: 'GET' });
      setDevices(res.devices);
    } catch (err) {
      setError((err as Error).message || 'Could not load devices.');
      setDevices([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function revoke(id: string) {
    setBusyId(id);
    setError(null);
    try {
      await http(`/auth/devices/${encodeURIComponent(id)}`, { method: 'DELETE' });
      notifySuccess('Device access revoked.');
      await load();
    } catch (err) {
      setError((err as Error).message || 'Could not revoke that device.');
    } finally {
      setBusyId(null);
      setConfirmId(null);
    }
  }

  const activeCount = devices?.filter((d) => d.isActive).length ?? 0;

  return (
    <div className="space-y-6">
      <Card className="p-5">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-heading font-medium text-fg-primary">Devices</h2>
            <p className="text-label text-fg-secondary">
              Tills registered to this business. Each active one holds a billing slot.
            </p>
          </div>
          <button
            type="button"
            onClick={() => void load()}
            className="inline-flex shrink-0 items-center gap-1.5 text-label text-fg-secondary hover:text-fg-primary"
          >
            <RotateCw size={13} /> Refresh
          </button>
        </div>

        {devices === null ? (
          <p className="text-body text-fg-tertiary">Loading devices…</p>
        ) : devices.length === 0 ? (
          <p className="text-body text-fg-tertiary">No devices registered yet.</p>
        ) : (
          <ul className="divide-y divide-stroke-hairline/10">
            {devices.map((d) => {
              const isThis = d.id === currentDeviceId;
              return (
                <li key={d.id} className="flex flex-wrap items-center gap-3 py-3">
                  <PlatformIcon platform={d.platform} active={d.isActive} />

                  <div className="min-w-[12rem] flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={cn('text-body', d.isActive ? 'text-fg-primary' : 'text-fg-tertiary')}>
                        {d.deviceName?.trim() || platformLabel(d.platform)}
                      </span>
                      {isThis && (
                        <span className="rounded bg-accent/10 px-1.5 py-0.5 text-label font-medium text-accent-ink">
                          This device
                        </span>
                      )}
                      {!d.isActive && (
                        <span className="rounded bg-bg-subtle px-1.5 py-0.5 text-label text-fg-tertiary">
                          Revoked
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-3 text-label text-fg-tertiary">
                      <Code>{d.id.slice(0, 16)}…</Code>
                      <span>{lastSync(d.lastSyncAt)}</span>
                    </div>
                  </div>

                  {/* The slot is the thing that matters and the thing nobody
                      would guess, so it explains itself. */}
                  <div className="shrink-0 text-right">
                    {d.deviceSlot != null ? (
                      <Explain
                        title="Billing slot"
                        className="text-label text-fg-secondary"
                        content={
                          <>
                            <ExplainRow label="Slot" value={String(d.deviceSlot).padStart(2, '0')} />
                            <p className="!mt-2 text-label text-fg-tertiary">
                              Every invoice this till issues carries this slot inside its
                              number, which is what stops two tills using the same number.
                              Revoking frees it; a later device that takes it continues the
                              series rather than restarting.
                            </p>
                          </>
                        }
                      >
                        Slot {String(d.deviceSlot).padStart(2, '0')}
                      </Explain>
                    ) : (
                      <span className="text-label text-fg-tertiary">No slot</span>
                    )}
                  </div>

                  {isOwner && d.isActive && (
                    <div className="shrink-0">
                      {confirmId === d.id ? (
                        <div className="flex items-center gap-1.5">
                          <Button
                            variant="danger"
                            size="sm"
                            disabled={busyId === d.id}
                            onClick={() => void revoke(d.id)}
                          >
                            {busyId === d.id ? 'Revoking…' : 'Confirm'}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setConfirmId(null)}>
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <Button variant="ghost" size="sm" onClick={() => setConfirmId(d.id)}>
                          Revoke
                        </Button>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {confirmId && (
          <p className="mt-3 text-label text-warning-ink">
            {confirmId === currentDeviceId
              ? 'This is the till you are using. Revoking it stops this browser billing until it registers again.'
              : 'That till stops billing immediately. Any sale it has not synced yet stays on the device.'}
          </p>
        )}

        {!isOwner && devices !== null && devices.length > 0 && (
          <p className="mt-3 text-label text-fg-tertiary">Only the owner can revoke a device.</p>
        )}
      </Card>

      <Card className="p-5">
        <h2 className="text-heading font-medium text-fg-primary">Why this matters</h2>
        <p className="mt-1.5 text-body-lg text-fg-secondary">
          A till that clears its browser data comes back as a new device and leaves the old
          one here, still holding a slot. Revoke the ones you no longer use — {activeCount} of{' '}
          {devices?.length ?? 0} registered {activeCount === 1 ? 'device is' : 'devices are'} active.
        </p>
      </Card>
    </div>
  );
}

function platformLabel(p: string | null): string {
  if (p === 'WEB') return 'Browser till';
  if (p === 'ANDROID') return 'Android device';
  if (p === 'IOS') return 'iPhone or iPad';
  if (p === 'DESKTOP') return 'Desktop till';
  return 'Unknown device';
}

function PlatformIcon({ platform, active }: { platform: string | null; active: boolean }) {
  const cls = cn('h-4 w-4 shrink-0', active ? 'text-fg-secondary' : 'text-fg-tertiary');
  if (platform === 'ANDROID' || platform === 'IOS') return <Smartphone className={cls} aria-hidden />;
  if (platform === 'DESKTOP') return <Monitor className={cls} aria-hidden />;
  return <Laptop className={cls} aria-hidden />;
}

/** Last sync in words. The age is the signal, not the timestamp. */
function lastSync(iso: string | null): string {
  if (!iso) return 'Never synced';
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60_000);
  if (mins < 2) return 'Synced just now';
  if (mins < 60) return `Synced ${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `Synced ${hrs} hr ago`;
  const days = Math.floor(hrs / 24);
  return `Synced ${days} day${days === 1 ? '' : 's'} ago`;
}
